import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  styled,
} from "@mui/material";
import Trash from "../../../assets/SVGs/trash_icon.svg";
import Styles from "../../Reusable/Forms/FormSteps/FinalStatus.module.css";
import RoutineStyle from "../../GlobalPolicies/RoutineUpload.module.css";
import { getFileImagePath } from "../utils/fileUtils";
import ExsitingBdCsvPopup from "../Popup/ExsitingBdCsvPopup";
import ReuploadComponent from "../utils/ReuploadComponent";
import { apiService } from "../../../api/apiService";
import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";

interface Content {
  suppliers: string;
  outputs: string;
  inputs: string;
  id: number;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  created_by?: string;
  status: string;
  bd_stage?: string;
  clients: string;
  is_existing_bd?: boolean;
  doc_path?: string;
  file_name?: string;
  file_size?: string;
  management?: string;
  function_l1?: string;
  function_l2?: string;
  manager_id?: string;
  approved_by?: string;
  reason_to_approve?: string;
}

interface SipocCardProps {
  row: Content;
  handleDelete?: (id: number) => void;
  handleClickOpen?: (id: number) => void;
  handleEdit: (id: number) => void;
  handleOpenModal: (id: string) => void;
  statusColors: Record<string, string>;
  onSuccess?: () => void;
}

const StyledTableRow = styled(Box)<{ status: string }>(({ status }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "10px",
  border: "1px solid #bbbbbb",
  boxShadow: `4px 0px 0px 0px ${status === "Approved"
    ? "#058B2B"
    : status === "Reverted"
      ? "#ffebeb"
      : status === "Pending"
        ? "#FF5C00"
        : "transparent"
    } inset`,
}));

const SipocCard: React.FC<SipocCardProps> = ({
  row,
  handleOpenModal,
  handleEdit,
  statusColors,
  handleClickOpen,
  onSuccess
}) => {
  const isValidDate = (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isNaN(date.getTime()) && date.getFullYear() > 1970;
  };
  const { instance } = useMsal();

  const [filePopupOpen, setFilePopupOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  const handleClose = () => {
    setFilePopupOpen(false);
    setSelectedFileId(null);
  };

  const handleViewClick = () => {
    setIsLoading(true);
    setSelectedFileId(row.id.toString());
    setFilePopupOpen(true);

    const handlePopupLoaded = () => {
      setIsLoading(false);
      window.removeEventListener('popup-loaded', handlePopupLoaded);
    };

    window.addEventListener('popup-loaded', handlePopupLoaded);
      setTimeout(() => {
      setIsLoading(false);
    }, 5000);
  };

  const renderSplitItems = (items: string) =>
    items.split("|").map((item, index) => (
      <Typography key={index} component="span" display="block">
        {item.trim()}
      </Typography>
    ));


  const handleReupload = async (itemId: string, file: File): Promise<void> => {  
      const formData = new FormData();
      formData.append("file", file);
  
  
      try {
        const response = await apiService(`/management/existing_business_desc/${itemId}`, "POST", formData, undefined, instance as PublicClientApplication);
  
        if (response.status !== "success") {
          throw new Error(response.message || "Failed to upload file");
        }
  
        // Refresh data after successful upload
        onSuccess?.();
      } catch (error) {
        console.error("Reupload error:", error);
        throw new Error(typeof error === 'string' ? error : "Failed to upload file");
      }
    };

  if (row.is_existing_bd) {
    return (
      <>
        <StyledTableRow sx={{ mb: 2 }} status={row.status}>
          <Paper
            key={row.id}
            className={RoutineStyle.bordercard}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                p: 2,
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  textAlign: "left",
                  gap: "5px",
                }}
              >
                {row.doc_path && (
                  <div>
                    <img
                      src={getFileImagePath(row.doc_path)}
                      alt="File"
                      height={50}
                      width={50}
                    />
                  </div>
                )}
                <div>
                  {row.file_name && (
                    <p>{row.file_name.split("_").slice(5).join("_")}</p>
                  )}
                  <h4>{row.file_size || ""}</h4>
                </div>
              </div>
              {row.function_l1 && (
                <div style={{ textAlign: "left", width: "20%" }}>
                  <h4>Function L1</h4>
                  <p>{row.function_l1}</p>
                </div>
              )}
              {row.function_l2 && (
                <div style={{ textAlign: "left", width: "20%" }}>
                  <h4>Function L2</h4>
                  <p>{row.function_l2}</p>
                </div>
              )}

              <div style={{ textAlign: "left", width: "20%" }}>
                <h4>Create Date</h4>
                <p>{new Date(row.created_at).toLocaleDateString()}</p>
              </div>
              <div style={{ textAlign: "left", width: "20%" }}>
                <h4>Status</h4>
                <p
                  className={RoutineStyle.statuspending}
                  style={{
                    color:
                      row.status === "Reverted"
                        ? "#F00"
                        : row.status === "Approved"
                          ? "#058B2B"
                          : row.status === "Pending"
                            ? "#FF5C00"
                            : "#000",
                  }}
                >
                  {row.status}
                </p>
              </div>
            </Box>
            <hr />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "flex-end",
                padding: "10px",
                borderRadius: "9px",
              }}
            >

              {row.status === "Reverted" && (
                <ReuploadComponent
                  itemId={row.id.toString()}
                  onReupload={handleReupload}
                  onSuccess={onSuccess}
                />
              )}

              {row.status === "Pending" && (
                <IconButton
                  size="small"
                  onClick={() => {
                    handleClickOpen && handleClickOpen(row.id);
                  }}
                >
                  <img src={Trash} alt="Trash Icon" />
                </IconButton>
              )}

              <Button
                onClick={handleViewClick}
                className={RoutineStyle.black_btn}
                disabled={isLoading}
              >
                {isLoading ? "loading..." : "View"}
              </Button>

              <ExsitingBdCsvPopup
                open={filePopupOpen}
                onClose={() => setFilePopupOpen(false)}
                fileId={selectedFileId}
                closePopup={handleClose}
                onSuccess={onSuccess}
              />
            </Box>
          </Paper>
        </StyledTableRow>
      </>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid #bbbbbb",
        borderRadius: "10px",
        marginBottom: "20px",
      }}
    >
      <Paper
        elevation={0}
        className={Styles.createNewBoxSipoc}
        sx={{ p: 2, borderRadius: "10px" }}
      >
        <Typography variant="h6" className={Styles.cardtitle}>
          SIPOC Diagram
        </Typography>
        <Box
          className={Styles.spoicdata}
          sx={{ display: "flex", flexWrap: "wrap" }}
        >
          {["Clients", "Inputs", "Outputs", "Suppliers"].map((field) => (
            <Box key={field} sx={{ width: "24%", padding: 1 }}>
              <Typography variant="h5" className={Styles.spoicdatatitle}>
                {field}
              </Typography>
              <Typography className={Styles.spoicdatatext}>
                {renderSplitItems(
                  String(row[field.toLowerCase() as keyof Content] || "")
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <div className={Styles.FinalList}>
        {/* Rest of your original card code */}
        <div className={Styles.FinalListDetails}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Last Update
            </Typography>
            <Typography variant="body1">
              {row.updated_at && isValidDate(row.updated_at)
                ? new Date(row.updated_at).toLocaleDateString()
                : "-"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created by
            </Typography>
            <Typography variant="body1">{row.created_by}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: statusColors[row.status] }}
            >
              {row.status}
            </Typography>
          </Box>
        </div>

        {/* Action buttons */}
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            gap: "20px",
            justifyContent: "flex-end",
          }}
        >
          {/* Original buttons code */}
          {row.status !== "Approved" && (
            <IconButton
              size="small"
              onClick={() => {
                handleClickOpen && handleClickOpen(row.id);
              }}
            >
              <img src={Trash} alt="Trash Icon" />
            </IconButton>
          )}

          {row.status === "Draft" && (
            <Button
              variant="outlined"
              sx={{
                color: "#000",
                borderColor: "#000",
                borderRadius: "10px",
                paddingLeft: "30px",
                paddingRight: "30px",
                textTransform: "capitalize",
              }}
              onClick={() => handleEdit(row.id)}
            >
              Edit
            </Button>
          )}

          {row.status === "Reverted" && (
            <>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#058B2B",
                  color: "#fff",
                  borderRadius: "10px",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  textTransform: "capitalize",
                }}
                onClick={() => handleOpenModal(row.id.toString())}
              >
                View
              </Button>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#FF0000",
                  color: "#fff",
                  borderRadius: "10px",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  textTransform: "capitalize",
                }}
                onClick={() => handleEdit(row.id)}
              >
                Resubmit
              </Button>
            </>
          )}

          {row.status === "Approved" && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#058B2B",
                color: "#fff",
                borderRadius: "10px",
                paddingLeft: "30px",
                paddingRight: "30px",
                textTransform: "capitalize",
              }}
              onClick={() => {
                setIsLoading(true);
                handleOpenModal(row.id.toString());
                setTimeout(() => {
                  setIsLoading(false);
                }, 3000);
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "View"}
            </Button>
          )}

          {row.status !== "Approved" && row.status !== "Reverted" && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: "10px",
                paddingLeft: "30px",
                paddingRight: "30px",
                textTransform: "capitalize",
              }}
              onClick={() => {
                setIsLoading(true);
                handleOpenModal(row.id.toString());
                setTimeout(() => {
                  setIsLoading(false);
                }, 3000);
              }}
            >
              View
            </Button>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default SipocCard;
